import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Printer, BookOpen, Loader2 } from "lucide-react";
import { CURRICULUM } from "@/lib/curriculum";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyWorkbookPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [modulesData, setModulesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/my-workbook`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => setModulesData(r.data?.modules || {}))
      .catch(() => setModulesData({}))
      .finally(() => setLoading(false));
  }, [token]);

  const hasAnswer = (v) => typeof v === "string" && v.trim().length > 0;
  const answeredModules = CURRICULUM.filter(
    (m) => modulesData && modulesData[m.id] && Object.values(modulesData[m.id].answers || {}).some(hasAnswer)
  );
  const totalAnswers = modulesData
    ? Object.values(modulesData).reduce((n, md) => n + Object.values(md.answers || {}).filter(hasAnswer).length, 0)
    : 0;

  return (
    <main className="min-h-screen pt-32 md:pt-40 pb-28" data-testid="my-workbook-page">
      <div className="relative z-10 max-w-[900px] mx-auto px-6 md:px-10">
        <button
          onClick={() => navigate("/dashboard")}
          className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] hover:text-[#B45309] mb-10 inline-flex items-center gap-2 transition-colors"
          data-testid="workbook-back"
        >
          <ArrowLeft size={12} /> Dashboard
        </button>

        <div className="flex items-start justify-between gap-4 mb-10">
          <div>
            <div className="section-tag mb-4">§ Your Workbook</div>
            <h1 className="font-display text-5xl md:text-6xl leading-[0.9] tracking-tight mb-3">
              Everything you&apos;ve written
            </h1>
            <p className="text-[#78716C] text-sm" data-testid="workbook-count">
              {totalAnswers} {totalAnswers === 1 ? "answer" : "answers"} saved across {answeredModules.length} of 8 modules.
            </p>
          </div>
          {totalAnswers > 0 && (
            <button onClick={() => window.print()} data-testid="workbook-print" className="btn-outline text-sm shrink-0">
              <Printer size={15} /> Print / Save PDF
            </button>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-[#78716C]" data-testid="workbook-loading">
            <Loader2 size={20} className="animate-spin text-[#B45309]" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Loading your workbook…</span>
          </div>
        )}

        {!loading && totalAnswers === 0 && (
          <div className="tacc-card p-10 text-center" data-testid="workbook-empty">
            <BookOpen size={32} className="text-[#B45309] mx-auto mb-4" strokeWidth={1.3} />
            <h3 className="font-heading text-xl tracking-tight mb-2">Your workbook is empty — for now</h3>
            <p className="text-[#78716C] text-sm max-w-md mx-auto mb-6">
              As you watch each module, pause on every reflection question and write your answer. It saves here automatically.
            </p>
            <button onClick={() => navigate("/modules/m1/experience")} className="btn-ember text-sm mx-auto" data-testid="workbook-start">
              Start Module 1
            </button>
          </div>
        )}

        {!loading && totalAnswers > 0 && (
          <div className="space-y-14">
            {answeredModules.map((m) => {
              const answers = modulesData[m.id]?.answers || {};
              return (
                <section key={m.id} data-testid={`workbook-module-${m.id}`}>
                  <div className="flex items-baseline gap-3 mb-6 pb-3 border-b border-[#E7DFD5]">
                    <span className="font-mono text-xs text-[#B45309]">{m.n}</span>
                    <h2 className="font-display text-2xl md:text-3xl tracking-tight">{m.title}</h2>
                  </div>
                  <div className="space-y-7">
                    {m.questions.map((q, i) =>
                      hasAnswer(answers[i]) ? (
                        <div key={`${m.id}-q${i}`} data-testid={`workbook-entry-${m.id}-${i}`}>
                          <div className="flex gap-3 mb-2">
                            <span className="font-mono text-[11px] text-[#B45309] mt-1 shrink-0">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <p className="text-[#44403C] text-sm md:text-base leading-relaxed">{q}</p>
                          </div>
                          <p className="ml-8 text-[#44403C] text-base md:text-lg leading-relaxed whitespace-pre-wrap border-l-2 border-[#F59E0B]/40 pl-4">
                            {answers[i]}
                          </p>
                        </div>
                      ) : null
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
